import { Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import React from "react";
import type { PhoneType } from "../../../../clients/graphqlTypes.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import type { OnboardingFormValues } from "./mergePrefillIntoFormValues.js";
import {
  ONBOARDING_FIELDS_SX,
  OnboardingAddressFields,
  OnboardingPhoneRow,
  FormSection,
} from "./OnboardingFormParts.js";
import { OnboardingLogoDropzone } from "./OnboardingLogoDropzone.js";
import {
  OnboardingSuggestionFrame,
  type OnboardingSuggestions,
} from "./OnboardingSuggestionFrame.js";

interface Props {
  /** What the website lookup filled, with the means to restore it. */
  suggestions?: OnboardingSuggestions;
  /** The type of the phone the organisation already has. */
  storedPhoneType?: PhoneType;
  /** "Tax and legal details" show after a lookup, or when the organisation stores such values. */
  showTaxSection: boolean;
  /**
   * Reaches the selects, which a disabled fieldset does not lock because MUI
   * renders them without a native form control.
   */
  disabled?: boolean;
  /** A logo found on the website is being imported. */
  logoImporting?: boolean;
}

/**
 * The fields of "Your business": logo, legal name, email and phone, the
 * address and, once a lookup ran or such data is stored, the managing director
 * with the tax and legal details. Nothing is required. The phone is the first
 * entry of the organisation's phone list; further entries are kept as they are.
 */
export const OnboardingBusinessFormFields = ({
  suggestions,
  storedPhoneType,
  showTaxSection,
  disabled = false,
  logoImporting = false,
}: Props) => {
  const { t } = useTranslate(["Address", "Global", "OrganisationProfile"]);
  const { values, setFieldValue } = useFormikContext<OnboardingFormValues>();

  return (
    <Stack spacing="40px" sx={ONBOARDING_FIELDS_SX}>
      <FormSection
        title={t("Company", { ns: "OrganisationProfile" })}
        subline={t("Shown on every quote, order and invoice", {
          ns: "OrganisationProfile",
        })}
      >
        <OnboardingLogoDropzone
          value={values.logo}
          onChange={logo => setFieldValue("logo", logo)}
          disabled={disabled}
          importing={logoImporting}
        />
        <OnboardingSuggestionFrame
          field="fullLegalName"
          suggestions={suggestions}
        >
          <TextField
            name="fullLegalName"
            label={t("Full legal company name", {
              ns: "OrganisationProfile",
            })}
            helperText={t("Exactly as registered", {
              ns: "OrganisationProfile",
            })}
            fullWidth
          />
        </OnboardingSuggestionFrame>
        <OnboardingSuggestionFrame field="email" suggestions={suggestions}>
          <TextField
            name="email"
            type="email"
            label={t("Company email", { ns: "OrganisationProfile" })}
            // Shown while the field is focused, as the label then sits above.
            placeholder={t("info@company.com", { ns: "OrganisationProfile" })}
            fullWidth
          />
        </OnboardingSuggestionFrame>
        <OnboardingPhoneRow
          phoneLabel={t("Company phone number", { ns: "OrganisationProfile" })}
          storedPhoneType={storedPhoneType}
          suggestions={suggestions}
          disabled={disabled}
        />
      </FormSection>
      <FormSection title={t("Address", { ns: "OrganisationProfile" })}>
        <OnboardingAddressFields
          suggestions={suggestions}
          disabled={disabled}
        />
      </FormSection>
      {showTaxSection && (
        <FormSection
          legal
          title={t("Tax and legal details", { ns: "OrganisationProfile" })}
          subline={t(
            "Needed before you send your first quote. Add them here or later in the settings.",
            { ns: "OrganisationProfile" }
          )}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: "16px",
            }}
          >
            <Box sx={{ gridColumn: "1 / -1" }}>
              <OnboardingSuggestionFrame
                field="managingDirector"
                suggestions={suggestions}
              >
                <TextField
                  name="managingDirector"
                  label={t("onboarding.managingDirector", {
                    ns: "OrganisationProfile",
                  })}
                  helperText={
                    suggestions?.states.managingDirector === "suggested"
                      ? t(
                          "Read from the imprint. Please check the spelling, it is printed on every quote.",
                          { ns: "OrganisationProfile" }
                        )
                      : undefined
                  }
                  fullWidth
                />
              </OnboardingSuggestionFrame>
            </Box>
            <TextField
              name="vatNumber"
              label={t("VAT ID", { ns: "OrganisationProfile" })}
              fullWidth
            />
            <TextField
              name="taxNumber"
              label={t("onboarding.taxNumber", { ns: "OrganisationProfile" })}
              fullWidth
            />
            <TextField
              name="registrationNumber"
              label={t("Trade register", { ns: "OrganisationProfile" })}
              fullWidth
            />
          </Box>
        </FormSection>
      )}
    </Stack>
  );
};
